/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { Customer, Event, Payment } from '../types/models';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationContext';
import { useCustomers, useEvents, usePayments, useDeleteEvent } from '@/hooks/useFirebaseQueries';

interface CrmContextType {
  customers: Customer[];
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
  refreshCustomers: () => void;
  
  events: Event[];
  selectedEvent: Event | null;
  setSelectedEvent: (event: Event | null) => void;
  refreshEvents: () => void;
  removeEvent: (eventId: string) => Promise<void>;
  
  payments: Payment[];
  refreshPayments: () => void;
}

const CrmContext = createContext<CrmContextType | null>(null);

export const useCrm = () => {
  const context = useContext(CrmContext);
  if (!context) {
    throw new Error('useCrm must be used within a CrmProvider');
  }
  return context;
};

interface CrmProviderProps {
  children: ReactNode;
}

export const CrmProvider: React.FC<CrmProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { addNotification, notifications } = useNotifications();
  
  const { data: customerData = [], refetch: refetchCustomers } = useCustomers();
  const { data: eventData = [], refetch: refetchEvents } = useEvents();
  
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  
  const { data: paymentData = [], refetch: refetchPayments } = usePayments(selectedEvent?.id);
  const deleteEventMutation = useDeleteEvent();

  // Stable user ID reference
  const userId = currentUser?.uid;

  // Memoized refresh functions (React Query handles caching, we just trigger refetch)
  const refreshCustomers = useCallback(() => { refetchCustomers(); }, [refetchCustomers]);
  const refreshEvents = useCallback(() => { refetchEvents(); }, [refetchEvents]);
  const refreshPayments = useCallback(() => { refetchPayments(); }, [refetchPayments]);

  const removeEvent = useCallback(async (eventId: string): Promise<void> => {
    try {
      await deleteEventMutation.mutateAsync(eventId);
    } catch (error) {
      console.error('Error removing event:', error);
      throw error;
    }
  }, [deleteEventMutation]);

  // Check for pending payment notifications (optimized)
  useEffect(() => {
    if (!userId || !eventData.length) return;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    eventData.forEach((event: Event) => {
      if (event.status === 'show_completed' && new Date(event.date) < sevenDaysAgo) {
        // We calculate total paid from paymentData if it's the selected event, else we rely on events having a totalPaid property (needs backend sync ideally)
        // For simplicity in context, we check if it triggers
        const totalCost = event.totalWithTax || event.cost;
        const totalPaid = 0; // In a full query migration, totalPaid should be calculated on the backend or inside useEvents hook
        
        if (totalPaid < totalCost) {
          const existingNotification = notifications.find(
            n => n.targetId === event.id && n.type === 'payment_due'
          );

          if (!existingNotification) {
            addNotification({
              type: 'payment_due',
              title: 'Cobro pendiente',
              message: `El evento "${event.title}" tiene un saldo pendiente.`,
              targetId: event.id,
              targetType: 'event',
            });
          }
        }
      }
    });
  }, [eventData, userId, notifications, addNotification]);

  const value = useMemo(() => ({
    customers: customerData as unknown as Customer[],
    selectedCustomer,
    setSelectedCustomer,
    refreshCustomers,
    
    events: eventData as unknown as Event[],
    selectedEvent,
    setSelectedEvent,
    refreshEvents,
    removeEvent,
    
    payments: paymentData as unknown as Payment[],
    refreshPayments,
  }), [
    customerData,
    selectedCustomer,
    refreshCustomers,
    eventData,
    selectedEvent,
    refreshEvents,
    removeEvent,
    paymentData,
    refreshPayments,
  ]);
  
  return <CrmContext.Provider value={value}>{children}</CrmContext.Provider>;
};
